import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';
import { useBranding } from '../lib/branding.jsx';
import { useEffect, useState } from 'react';
import { connectWS, api } from '../lib/api.js';
import { useTenantHref, useTenantSlug } from '../lib/tenantPath.jsx';

const NAV = [
  { to: '/',            label: 'Dashboard'  },
  { to: '/patients',    label: 'Patients'   },
  { to: '/deliveries',  label: 'Deliveries' },
  { to: '/alerts',      label: 'Alerts'     },
  { to: '/kpis',        label: 'KPIs'       },
  { to: '/admin',       label: 'Admin'      },
  { to: '/admin/users', label: 'Users',         adminOnly: true },
  { to: '/admin/lookup',label: 'PMS Lookup'                      },
  { to: '/admin/branding',      label: 'Branding',       adminOnly: true },
  { to: '/admin/payments',      label: 'Payments',       adminOnly: true },
  { to: '/admin/payments-audit',label: 'Payments Audit', adminPharmacist: true },
  { to: '/shipments',           label: 'Shipments' },
  { to: '/watchlist',           label: 'Watchlist',      adminPharmacist: true },
  { to: '/help/faq',            label: 'Help & FAQ' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const location = useLocation();
  const [openAlerts, setOpenAlerts] = useState(0);
  const [wsStatus, setWsStatus] = useState('connecting');
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Phase 8.4 — when rendered under /t/:slug/*, every NavLink gets prefixed
  // automatically via tHref(); on legacy URLs (slug == null) it's a passthrough.
  const tHref = useTenantHref();
  const tenantSlug = useTenantSlug();

  async function refreshAlertCount() {
    try { const r = await api.alerts({ status: 'open' }); setOpenAlerts(r.alerts.length); } catch {}
  }

  useEffect(() => {
    refreshAlertCount();
    const ws = connectWS((m) => {
      setWsStatus('connected');
      if (m.type === 'alert_updated' || m.type === 'integration_event') refreshAlertCount();
    });
    ws.onopen = () => setWsStatus('connected');
    ws.onclose = () => setWsStatus('disconnected');
    const interval = setInterval(refreshAlertCount, 30_000);
    return () => { ws.close(); clearInterval(interval); };
  }, []);

  // Close the mobile drawer on every route change so navigation doesn't leave it stuck open.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Disable body scroll while drawer is open on mobile
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const visible = NAV.filter(n => {
    if (n.adminOnly       && user?.role !== 'admin') return false;
    if (n.adminPharmacist && user?.role !== 'admin' && user?.role !== 'pharmacist') return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile top bar — only shown below lg */}
      <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 flex items-center gap-3 px-3 py-2"
              style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}>
        <button
          aria-label="Open navigation"
          onClick={() => setDrawerOpen(true)}
          className="p-2 -ml-1 rounded hover:bg-slate-100 active:bg-slate-200">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {branding.logo_url
            ? <img src={branding.logo_url} alt="" className="w-7 h-7 rounded shrink-0" onError={(e) => e.target.style.display='none'} />
            : <div className="w-7 h-7 rounded shrink-0" style={{ background: 'var(--brand-primary)' }} />}
          <div className="font-bold text-base truncate" style={{ color: 'var(--brand-primary)' }}>
            {branding.display_name || 'M2B'}
          </div>
        </div>
        <span className={`w-2 h-2 rounded-full shrink-0 ${wsStatus === 'connected' ? 'bg-emerald-500' : 'bg-slate-300'}`}
              title={`Real-time: ${wsStatus}`} />
        {openAlerts > 0 && (
          <NavLink to={tHref('/alerts')} className="badge-critical">{openAlerts}</NavLink>
        )}
      </header>

      <div className="lg:flex">
        {/* Sidebar — drawer on mobile, persistent rail on lg+ */}
        {drawerOpen && <div className="drawer-scrim" onClick={() => setDrawerOpen(false)} />}
        <aside
          className={`
            fixed top-0 bottom-0 left-0 w-64 z-40 bg-white border-r border-slate-200 flex flex-col
            transform transition-transform duration-200 ease-out
            ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}
            lg:static lg:translate-x-0 lg:w-60 lg:z-auto lg:shrink-0
          `}
          style={{ paddingTop: 'env(safe-area-inset-top)' }}>
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              {branding.logo_url
                ? <img src={branding.logo_url} alt="" className="w-8 h-8 rounded shrink-0" onError={(e) => e.target.style.display='none'} />
                : <div className="w-8 h-8 rounded shrink-0" style={{ background: 'var(--brand-primary)' }} />}
              <div className="min-w-0">
                <div className="font-bold text-base leading-tight truncate" style={{ color: 'var(--brand-primary)' }}>
                  {branding.display_name || 'M2B'}
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1.5">
                  <span>Meds-to-Beds</span>
                  {tenantSlug && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-slate-100 text-slate-600 border border-slate-200"
                          title="Tenant in URL scope">
                      /t/{tenantSlug}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              aria-label="Close navigation"
              onClick={() => setDrawerOpen(false)}
              className="lg:hidden p-1 -mr-1 rounded hover:bg-slate-100">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="6" y1="6" x2="18" y2="18"/><line x1="6" y1="18" x2="18" y2="6"/>
              </svg>
            </button>
          </div>
          <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto">
            {visible.map(n => (
              <NavLink key={n.to} to={tHref(n.to)} end={n.to === '/' || n.to === '/admin'}
                className={({ isActive }) => `flex items-center justify-between px-3 py-2 rounded-md text-sm ${
                  isActive ? 'font-medium' : 'text-slate-700 hover:bg-slate-50'
                }`}
                style={({ isActive }) => isActive ? { background: 'color-mix(in srgb, var(--brand-primary) 12%, white)', color: 'var(--brand-primary)' } : undefined}>
                <span>{n.label}</span>
                {n.to === '/alerts' && openAlerts > 0 && (
                  <span className="badge-critical">{openAlerts}</span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-slate-200 text-xs"
               style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
            <div className="mb-2 flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${wsStatus === 'connected' ? 'bg-emerald-500' : 'bg-slate-300'}`} />
              <span className="text-slate-500">Real-time: {wsStatus}</span>
            </div>
            <div className="text-slate-700 font-medium truncate">{user?.full_name}</div>
            <div className="text-slate-500">{user?.role}</div>
            <button onClick={logout} className="mt-2 hover:underline" style={{ color: 'var(--brand-primary)' }}>Sign out</button>
          </div>
        </aside>

        <main className="flex-1 min-w-0 overflow-x-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6"
               style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
